import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Edit, ArrowBack } from '@mui/icons-material';
import { Notifier } from '../../../system/services/notificationManager';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import {
  usePlmContractQuery,
  usePlmContractDetailEditSubSubscription,
} from '../../../../types/generated-types';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import BorderedSection from '../../shared/borderedSection';
import { useInjectableComponents } from '../../../system/services/injectableComponentsManager';
import { useIsSmallScreen } from '../../../system/SystemMediaQueriesProvider';
import '../ui/plm.css';

export const PlmContractDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuthorizer();
  const { setTitle, setTitlePath } = useInjectableComponents();
  const isSmall = useIsSmallScreen();

  const { data, loading, error } = usePlmContractQuery({
    variables: { _id: id },
    fetchPolicy: 'cache-and-network',
  });

  usePlmContractDetailEditSubSubscription({
    variables: { id: id! },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  React.useEffect(() => {
    setTitle(isSmall ? 'PLM Contract' : 'Peak Load Management Contract');
    setTitlePath('/plmContracts');
    return () => {
      setTitle(undefined);
      setTitlePath(undefined);
    };
  }, [setTitle, setTitlePath, isSmall]);

  React.useEffect(() => {
    if (error) {
      Notifier.error('Error loading PLM contract: ', error);
    }
  }, [error]);

  if (loading) {
    return (
      <Box
        component="div"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Box>
          <Typography variant="h4" color="secondary">
            Preparing your data...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error || !data?.plmContract) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
        sx={{ padding: '16px' }}
      >
        <Typography color="error">
          Error loading PLM contract: {error?.message || 'Contract not found'}
        </Typography>
      </Box>
    );
  }

  const plmContract = data.plmContract as any;

  return (
    <Box sx={{ padding: '16px' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Typography variant="h4">PLM Contract Details</Typography>
        </Box>
        {can('update', 'PlmContract') && (
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate(`/plmContracts/${id}/edit-plmContract`)}
          >
            Edit
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <BorderedSection title="Contract Information">
            <Grid container spacing={2} className="plm-inner-grid-container">
              <Grid item className="item-wrapper" xs={6}>
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Name:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {plmContract.name}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Program:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {plmContract.program
                            ? `${plmContract.program.name} (${plmContract.program.partner?.name || 'Unknown partner'})`
                            : plmContract.programId || 'No Program'}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Minimum Reduction (kW):
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {plmContract.minimumReductionKW}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Baseline Method:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {plmContract.baselineMethod}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Penalty for Non-Compliance:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {plmContract.penaltyForNonCompliance ||
                            'Not specified'}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item className="item-wrapper" xs={6}>
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Contract ID:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {plmContract.contractId}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Maximum Events per Season:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {plmContract.maximumEventsPerSeason}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Incentive:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {plmContract.incentiveDescription || 'Not specified'}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Status:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          <Chip
                            label={plmContract.status}
                            color={
                              plmContract.status === 'active'
                                ? 'success'
                                : plmContract.status === 'draft'
                                  ? 'default'
                                  : 'error'
                            }
                            size="small"
                            sx={{ borderRadius: '4px', minWidth: '125px' }}
                          />
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item className="item-wrapper" xs={12}>
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Notification Email:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {plmContract.notificationEmail}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </BorderedSection>
        </Grid>

        <Grid item xs={12}>
          <BorderedSection title="Subscribed Properties">
            <Grid container spacing={2} className="plm-inner-grid-container">
              <Grid item className="item-wrapper" xs={6}>
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <b>Property:</b>
                    {plmContract.properties &&
                    plmContract.properties.length > 0 ? (
                      plmContract.properties.map(
                        (property: any, index: number) => (
                          <ListItemText
                            key={index}
                            className="plm-listItem-text"
                          >
                            {property?.title || 'Untitled Property'}
                          </ListItemText>
                        ),
                      )
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No properties subscribed to this contract
                      </Typography>
                    )}
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </BorderedSection>
        </Grid>

        <Grid item xs={12}>
          <BorderedSection title="Utility">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {plmContract.partner ? (
                  <Typography variant="body1">
                    {plmContract.partner.name}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No partner assigned
                  </Typography>
                )}
              </Grid>
            </Grid>
          </BorderedSection>
        </Grid>

        <Grid item xs={12}>
          <BorderedSection title="Dispatchers">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {plmContract.dispatchers &&
                plmContract.dispatchers.length > 0 ? (
                  <Box display="flex" flexDirection="column" gap={1}>
                    {plmContract.dispatchers.map(
                      (dispatcher: any, index: number) => (
                        <Typography key={index} variant="body1">
                          {dispatcher?.name || 'Unknown Dispatcher'}
                        </Typography>
                      ),
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No dispatchers assigned to this contract
                  </Typography>
                )}
              </Grid>
            </Grid>
          </BorderedSection>
        </Grid>
      </Grid>
    </Box>
  );
};
